import React, { useState } from 'react';
import { Box, Typography, Paper, Button, Tooltip } from '@mui/material';
import { useTheme } from '@emotion/react';
import { tokens } from '../theme';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

/**
 * CartilageMap Component
 * Eklem kıkırdağı haritası için interaktif görsel bileşen
 * ICRS (International Cartilage Repair Society) haritalama sistemi
 * 
 * DÜZELTME: Görsel oranları korunuyor, çizilen alanlar doğru pozisyonda görünüyor
 */
const CartilageMap = ({ 
  imageUrl, 
  areas = [], 
  selectedArea = null, 
  selectedAreas = [], // Çoklu seçim için
  onAreaSelect = () => {},
  disabled = false,
  showLabels = true,
  multiSelect = false, // Çoklu seçim modu
  showOnlySelectedButton = false, // "Sadece Seçilenleri Göster" butonu göster
  showOnlySelected = false, // Sadece seçilenleri göster durumu
  onToggleShowOnlySelected = () => {} // Toggle fonksiyonu
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [hoveredArea, setHoveredArea] = useState(null);
  const [showAllLabels, setShowAllLabels] = useState(false);
  
  // Alanın seçili olup olmadığını kontrol et
  const isAreaSelected = (area) => {
    if (multiSelect) {
      return selectedAreas.some(a => a.id === area.id);
    }
    return selectedArea?.id === area.id;
  };

  // Varsayılan eklem görsel URL'i (örnek - kendi görselinizi ekleyebilirsiniz)
  const defaultImageUrl = imageUrl || '/assets/knee-cartilage-map.png';

  const handleAreaClick = (area) => {
    if (!disabled) {
      onAreaSelect(area);
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 3,
        backgroundColor: colors.primary[400],
        border: `2px solid ${colors.blueAccent[700]}`,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 'bold',
            color: colors.greenAccent[400],
          }}
        >
          🦴 Eklem Kıkırdağı Haritalama (ICRS)
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {showLabels && areas.length > 0 && (
            <Tooltip title={showAllLabels ? "Etiketleri Gizle" : "Etiketleri Göster"}>
              <Button
                size="small"
                variant={showAllLabels ? "contained" : "outlined"}
                onClick={() => setShowAllLabels(!showAllLabels)}
                startIcon={showAllLabels ? <VisibilityOffIcon /> : <VisibilityIcon />}
                sx={{
                  borderColor: colors.blueAccent[500],
                  color: showAllLabels ? 'white' : colors.blueAccent[400],
                  backgroundColor: showAllLabels ? colors.blueAccent[600] : 'transparent',
                  '&:hover': {
                    backgroundColor: showAllLabels ? colors.blueAccent[700] : colors.blueAccent[900]
                  }
                }}
              >
                {showAllLabels ? 'Etiketleri Gizle' : 'Etiketleri Göster'}
              </Button>
          </Tooltip>
          )}
          
          {showOnlySelectedButton && selectedAreas.length > 0 && (
            <Tooltip title={showOnlySelected ? "Tüm Alanları Göster" : "Sadece Seçilenleri Göster"}>
              <Button
                size="small"
                variant={showOnlySelected ? "contained" : "outlined"}
                onClick={onToggleShowOnlySelected}
                startIcon={showOnlySelected ? <FilterAltOffIcon /> : <FilterAltIcon />}
                sx={{
                  borderColor: colors.greenAccent[500],
                  color: showOnlySelected ? 'white' : colors.greenAccent[400],
                  backgroundColor: showOnlySelected ? colors.greenAccent[600] : 'transparent',
                  '&:hover': {
                    backgroundColor: showOnlySelected ? colors.greenAccent[700] : colors.greenAccent[900]
                  }
                }}
              >
                {showOnlySelected ? 'Tüm Alanları Göster' : 'Sadece Seçilenleri Göster'}
              </Button>
          </Tooltip>
          )}
        </Box>
      </Box>

      <Box 
        sx={{ 
          position: 'relative', 
          width: '100%',
          backgroundColor: colors.primary[600],
          borderRadius: 2,
          overflow: 'hidden',
          border: `3px solid ${colors.greenAccent[600]}`,
        }}
      >
        {/* Görsel - Orijinal aspect ratio korunuyor */}
        <img
          src={defaultImageUrl}
          alt="Cartilage Map"
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
            opacity: 0.8
          }}
          onError={(e) => {
            // Görsel yüklenemezse placeholder göster
            e.target.style.display = 'none';
          }}
        />
          
        {/* Alanlar üzerine interaktif bölgeler - Görsel ile aynı boyutta */}
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: disabled ? 'none' : 'auto'
          }}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          {areas.map((area, index) => (
            <g key={index}>
              {/* Tıklanabilir alan */}
              <path
                d={area.path}
                fill={area.color || colors.greenAccent[500]}
                stroke={
                  isAreaSelected(area)
                    ? '#FFD700'  // Parlak altın sarısı
                    : hoveredArea?.id === area.id
                    ? colors.blueAccent[300]
                    : (area.color || colors.greenAccent[400])
                }
                strokeWidth={
                  isAreaSelected(area) 
                    ? 1.2  // Seçili olanlarda biraz kalın
                    : hoveredArea?.id === area.id 
                    ? 1.2 
                    : 0.8
                }
                fillOpacity={
                  isAreaSelected(area)
                    ? 0.6
                    : hoveredArea?.id === area.id
                    ? 0.4
                    : 0.3
                }
                strokeOpacity={isAreaSelected(area) ? 1 : 0.9}
                style={{
                  cursor: disabled ? 'default' : 'pointer',
                  transition: 'all 0.3s ease',
                  filter: isAreaSelected(area) ? 'brightness(1.4) saturate(1.2) drop-shadow(0 0 4px rgba(255, 215, 0, 0.6))' : 'none'
                }}
                onClick={() => handleAreaClick(area)}
                onMouseEnter={() => setHoveredArea(area)}
                onMouseLeave={() => setHoveredArea(null)}
              />
            </g>
          ))}
        </svg>

        {/* Alan etiketleri - Sabit boyutta text için */}
        {showLabels && areas.map((area, index) => {
          if (!area.label) return null;
          
          // Etiket görünürlük kontrolü
          // showAllLabels false ise sadece hover edilen gösterilir
          // showAllLabels true ise tümü gösterilir
          const shouldShowLabel = showAllLabels 
                                  ? true  // Tümünü göster
                                  : hoveredArea?.id === area.id;  // Sadece hover edileni göster
          
          if (!shouldShowLabel) return null;
          
          return (
            <div
              key={index}
          style={{
            position: 'absolute',
                left: `${area.labelX}%`,
                top: `${area.labelY}%`,
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'opacity 0.2s ease',
                opacity: shouldShowLabel ? 1 : 0
              }}
            >
              <div
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.85)',
                  color: '#ffffff',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  fontFamily: 'Arial, sans-serif',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  lineHeight: '14px',
                  minHeight: '22px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  border: isAreaSelected(area)
                    ? `2px solid ${colors.greenAccent[400]}` 
                    : 'none'
                }}
              >
                {area.label}
              </div>
            </div>
          );
        })}
      </Box>

      {/* Seçilen alan bilgisi */}
      {selectedArea && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: colors.greenAccent[800],
            borderRadius: 2,
            border: `2px solid ${colors.greenAccent[500]}`,
          }}
        >
          <Typography variant="body1" sx={{ color: colors.grey[100] }}>
            <strong>Seçilen Alan:</strong> {selectedArea.name}
          </Typography>
          {selectedArea.description && (
            <Typography variant="body2" sx={{ color: colors.grey[300], mt: 1 }}>
              {selectedArea.description}
            </Typography>
          )}
        </Box>
      )}

      {/* Bilgi kutusu */}
      <Box
        sx={{
          mt: 2,
          p: 1.5,
          backgroundColor: colors.blueAccent[900],
          borderRadius: 2,
          border: `1px solid ${colors.blueAccent[700]}`,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: colors.blueAccent[300],
            fontStyle: 'italic',
            display: 'block'
          }}
        >
          💡 {disabled 
            ? (showOnlySelected 
                ? `Sadece seçilen alanlar gösteriliyor (${selectedAreas.length} alan)`
                : (showAllLabels
                    ? 'Tüm etiketler görünür'
                    : 'Etiketler gizli - Mouse ile alana gelin veya "Etiketleri Göster" butonuna tıklayın'))
            : showAllLabels 
              ? 'Tüm etiketler görünür - Alanlara tıklayarak seçim yapabilirsiniz'
              : 'Etiketleri görmek için alana mouse ile gelin veya "Etiketleri Göster" butonuna tıklayın'
          }
          </Typography>
        </Box>
    </Paper>
  );
};

export default CartilageMap;